import type { AccountEntity } from '@actual-app/core/types/models';

import { isAccountFailedSync } from '#accounts/syncStatus';

import type { ContaFixa } from './contasFixas';

/**
 * Motor da tela **Pendências** (spec §5) — a fila do que precisa de atenção.
 *
 * ⭐ PURO de propósito, como `contasFixas.ts`: não busca nada, não conhece
 * React e não decide DE ONDE vêm os dados (isso é `usePendencias.ts`). Ele
 * só recebe lançamentos, contas e contas fixas e devolve as duas faixas na
 * ordem certa — o que faz cada trava abaixo ser testável linha a linha, sem
 * banco e sem render.
 *
 * ⚠️⚠️ POR QUE ISTO EXISTE: a primeira sincronização do Open Finance
 * (Pluggy) trouxe **1.125 lançamentos sem categoria de 10 contas** e não há
 * caminho nenhum no app pra corrigir isso conta por conta. O que esta tela
 * vende é UMA coisa: confiança no número. Um contador que mente (contando
 * um lançamento-pai de split, por exemplo) destrói exatamente isso — é o
 * risco R4 do spec, e é o motivo das travas serem explícitas e nomeadas.
 */

/**
 * Um lançamento cru, como sai da consulta de `uncategorizedTransactions()`
 * (ver `usePendencias.ts`). Os nomes são os do NOSSO vocabulário; o mapeamento
 * campo-a-campo com o schema AQL do Actual está no hook.
 */
export type LancamentoCru = {
  id: string;
  /** `YYYY-MM-DD`. */
  date: string;
  /** Centavos, com sinal (negativo = saiu dinheiro). */
  amount: number;
  /** `payee.name` — o nome já limpo/normalizado pelo Actual. */
  nomeDeQuemRecebe: string | null;
  /**
   * `imported_payee` — o nome SUJO que o banco mandou (`PAG*MERCADOMUNDIAL`).
   * No Open Finance é ELE que se repete mês a mês; ver o JSDoc de
   * `Pendencias.tsx` sobre o aprendizado automático.
   */
  nomeImportado: string | null;
  /** `account.name`. */
  conta: string | null;
  /** `is_parent` — lançamento-PAI de um split. */
  ehPai: boolean;
  /** `account.offbudget`. */
  contaForaDoOrcamento: boolean;
  /** `payee.transfer_acct` — preenchido só quando é transferência. */
  contaDeTransferencia: string | null;
  /** `payee.transfer_acct.offbudget`; `null` quando não é transferência. */
  transferenciaForaDoOrcamento: boolean | null;
  /** `category` — `true` quando JÁ tem categoria. */
  temCategoria: boolean;
};

/** Um lançamento que entrou na faixa "Sem categoria", pronto pra tela. */
export type LancamentoPendente = {
  id: string;
  data: string;
  /** O que aparece em destaque na linha. */
  descricao: string;
  /** O nome sujo do banco — é com ele que a regra de aprendizado é escrita. */
  nomeImportado: string | null;
  /** Centavos, com sinal. */
  valor: number;
  conta: string | null;
};

export type TipoUrgente = 'banco-desconectado' | 'fixa-vencida';

/** Um item da faixa "Precisa agora". */
export type ItemUrgente = {
  id: string;
  tipo: TipoUrgente;
  titulo: string;
  /** Centavos, positivo — só nas fixas vencidas; `null` no banco desconectado. */
  valor: number | null;
  /** Rota interna pra onde o item leva, quando existe. */
  destino: string | null;
};

export type Fila = {
  precisaAgora: ItemUrgente[];
  semCategoria: LancamentoPendente[];
  /**
   * Soma dos VALORES ABSOLUTOS dos lançamentos sem categoria, em centavos.
   *
   * ⚠️ Absoluto de propósito: a faixa mistura entrada e saída, e somar com
   * sinal faria R$ 500 de salário anular R$ 500 de mercado e exibir "R$ 0,00
   * sem categoria" com duas linhas na tela — o caso clássico de número que
   * mente calado.
   */
  totalSemCategoria: number;
  /** `precisaAgora.length + semCategoria.length` — o número do menu. */
  total: number;
};

/**
 * Uma conta do dono, no mínimo que o motor precisa saber.
 * `bank_sync_status` é lido pela função NATIVA `isAccountFailedSync` — não
 * reescrevemos a régua de "o que é falha" aqui.
 */
export type ContaDoDono = {
  id: string;
  name: string;
  /**
   * O MESMO tipo do Actual (`AccountEntity['bank_sync_status']`), não um
   * `string` solto: é ele que garante, no compilador, que um status novo
   * inventado pelo upstream não entre aqui em silêncio.
   */
  bank_sync_status?: AccountEntity['bank_sync_status'];
  closed?: boolean | null;
};

/** Uma conta fixa do mês com a data de vencimento junto (ver `contasFixas.ts`). */
export type FixaComVencimento = ContaFixa;

export type EntradaDaFila = {
  lancamentos: LancamentoCru[];
  contas: ContaDoDono[];
  /**
   * Ids das contas que falharam na sincronização DESTA SESSÃO
   * (`useFailedAccounts`, estado do redux). É complementar — e não
   * substituto — do `bank_sync_status` persistido: ao abrir o app sem ter
   * rodado sync nenhuma, esse conjunto está vazio e só o status persistido
   * sabe que o Pluggy pediu reautorização.
   */
  falhasDaSessao: ReadonlySet<string>;
  contasFixas: FixaComVencimento[];
  /** `YYYY-MM-DD` — hoje. Injetado pra o motor não depender do relógio. */
  hoje: string;
};

/**
 * ⭐⭐ AS TRAVAS — cada motivo de um lançamento NÃO entrar na fila, nomeado e
 * isolado numa função só.
 *
 * Isolar cada trava num item desta lista não é estilo: é o que permite o
 * **controle negativo** exigido pelo spec §7 — desarmar UMA trava
 * (`TRAVAS.filter(t => t.id !== 'split-pai')`) e provar que isso derruba
 * exatamente o teste dela e nenhum outro. Um controle que passa nos dois
 * estados não é prova. Ver `filaPendencias.test.ts`.
 */
export type Trava = {
  id: 'ja-classificado' | 'split-pai' | 'transferencia-interna' | 'conta-fora';
  /** Por que este lançamento não é trabalho pro dono. */
  motivo: string;
  /** `true` = o lançamento fica DE FORA da fila. */
  ficaDeFora: (l: LancamentoCru) => boolean;
};

export const TRAVAS: readonly Trava[] = [
  {
    id: 'ja-classificado',
    motivo: 'já tem categoria — não é pendência',
    ficaDeFora: l => l.temCategoria,
  },
  {
    /**
     * ⚠️⚠️ RISCO R4 do spec, a trava que mais importa. Um split é UM
     * lançamento-pai (sem categoria, sempre) mais N filhos, e quem recebe
     * categoria são os FILHOS — o pai nunca. Contar o pai infla a fila com
     * um item que o dono não tem como resolver: ele já classificou tudo e o
     * contador continua marcando 1.
     *
     * ⚠️ O executor AQL do Actual já exclui o pai quando a consulta roda com
     * `splits: 'inline'` (o padrão) — `execTransactionsBasic` acrescenta
     * `is_parent = 0` ao WHERE. A trava aqui é DELIBERADAMENTE redundante:
     * ela é a que está sob teste, e é ela que segura o número se um dia
     * alguém passar `splits: 'grouped'`/`'all'` na consulta (o que traria os
     * pais de volta, em silêncio, sem quebrar nada).
     */
    id: 'split-pai',
    motivo: 'é o pai de um split — quem recebe categoria são os filhos',
    ficaDeFora: l => l.ehPai,
  },
  {
    /**
     * Transferência ENTRE CONTAS ON-BUDGET não é gasto nem receita: é o mesmo
     * dinheiro mudando de lugar, e o Actual de propósito não pede categoria
     * pra ela. Transferência pra conta FORA do orçamento (`offbudget: true`)
     * continua na fila — ali o dinheiro saiu do orçamento de verdade.
     */
    id: 'transferencia-interna',
    motivo: 'transferência entre contas do orçamento — não pede categoria',
    ficaDeFora: l =>
      l.contaDeTransferencia !== null &&
      l.transferenciaForaDoOrcamento !== true,
  },
  {
    id: 'conta-fora',
    motivo: 'conta fora do orçamento — não entra no mês',
    ficaDeFora: l => l.contaForaDoOrcamento,
  },
];

/**
 * Aplica as travas e converte o que sobra em linha de tela.
 *
 * ⚠️ `travas` é OBRIGATÓRIO — sem default, de propósito. É a mesma lição do
 * `escopo` em `montarContasFixas`: um default aqui seria um SILENCIADOR
 * (alguém remove o argumento, o typecheck passa e a trava do split morre sem
 * ninguém perceber). Quem quer a lista padrão passa `TRAVAS` por escrito; quem
 * quer desarmar uma (só o teste de controle negativo deveria querer) escreve
 * isso explicitamente.
 */
export function filtrarSemCategoria(
  lancamentos: LancamentoCru[],
  travas: readonly Trava[],
): LancamentoPendente[] {
  return lancamentos
    .filter(l => !travas.some(trava => trava.ficaDeFora(l)))
    .map(l => ({
      id: l.id,
      data: l.date,
      // O nome limpo é o que o dono reconhece; o sujo do banco é o fallback
      // (transação importada cujo payee ainda não foi resolvido).
      descricao: l.nomeDeQuemRecebe ?? l.nomeImportado ?? '',
      nomeImportado: l.nomeImportado,
      valor: l.amount,
      conta: l.conta,
    }))
    .sort((a, b) =>
      a.data === b.data ? a.id.localeCompare(b.id) : a.data < b.data ? 1 : -1,
    );
}

/**
 * Contas cuja sincronização está quebrada — no Pluggy é o pedido periódico de
 * reautorização, que se ignorado faz o extrato parar de chegar em silêncio.
 *
 * União de DUAS fontes, de propósito: o status PERSISTIDO na conta
 * (`bank_sync_status`, via a função nativa `isAccountFailedSync`) e as falhas
 * DESTA SESSÃO (`useFailedAccounts`, redux). Nenhuma das duas sozinha cobre os
 * dois momentos — abrir o app num dia qualquer (só a persistida sabe) e a sync
 * que acabou de falhar agora (só a da sessão sabe).
 *
 * Conta FECHADA fica de fora: não há o que reautorizar.
 */
function bancosDesconectados(
  contas: ContaDoDono[],
  falhasDaSessao: ReadonlySet<string>,
): ItemUrgente[] {
  return contas
    .filter(c => !c.closed)
    .filter(
      c =>
        falhasDaSessao.has(c.id) ||
        isAccountFailedSync({ bank_sync_status: c.bank_sync_status ?? null }),
    )
    .map(c => ({
      id: `banco-${c.id}`,
      tipo: 'banco-desconectado' as const,
      titulo: c.name,
      valor: null,
      destino: `/accounts/${c.id}`,
    }));
}

/**
 * Conta fixa VENCIDA sem pagamento encontrado.
 *
 * ⭐ Reusa o motor que já existe: `realizado === null` já significa "nenhuma
 * transação deste mês aponta pra este agendado" (ver `contasFixas.ts`) — não
 * existe, nem aqui nem lá, um campo manual de "paguei". Este motor só
 * acrescenta a pergunta que a tela Mês não fazia: **já passou da data?**
 *
 * ⚠️ `vencimento === null` (agendado sem `next_date`) NÃO vira pendência.
 * Sem data não há como afirmar que venceu, e inventar uma pendência é pior
 * que deixar uma de fora: a fila perde a credibilidade que ela existe pra
 * ter.
 *
 * ⚠️ `vencimento < hoje`, nunca `<=`: conta que vence HOJE ainda não está
 * vencida — cobrar o dono no próprio dia do vencimento é ruído.
 */
function fixasVencidas(
  contasFixas: FixaComVencimento[],
  hoje: string,
): ItemUrgente[] {
  return contasFixas
    .filter(
      c =>
        c.realizado === null &&
        c.vencimento !== null &&
        c.vencimento !== undefined &&
        c.vencimento < hoje,
    )
    .map(c => ({
      id: `fixa-${c.id}`,
      tipo: 'fixa-vencida' as const,
      titulo: c.nome,
      valor: c.planejado,
      destino: null,
    }));
}

/**
 * Monta a fila inteira, nas duas faixas e na ordem do spec §5.1:
 * **Precisa agora** (banco desconectado, depois fixa vencida) e
 * **Sem categoria** (mais recente primeiro).
 *
 * Banco desconectado vem antes da fixa vencida porque é o item que, enquanto
 * não for resolvido, faz a fila TODA mentir — sem sincronizar, o lançamento
 * que classificaria a fixa nem chegou ao app.
 */
export function montarFila({
  lancamentos,
  contas,
  falhasDaSessao,
  contasFixas,
  hoje,
}: EntradaDaFila): Fila {
  const precisaAgora = [
    ...bancosDesconectados(contas, falhasDaSessao),
    ...fixasVencidas(contasFixas, hoje),
  ];
  const semCategoria = filtrarSemCategoria(lancamentos, TRAVAS);

  return {
    precisaAgora,
    semCategoria,
    totalSemCategoria: semCategoria.reduce((s, l) => s + Math.abs(l.valor), 0),
    total: precisaAgora.length + semCategoria.length,
  };
}

/** A fila vazia — o estado SAUDÁVEL (spec §5.4), não um erro. */
export const FILA_VAZIA: Fila = {
  precisaAgora: [],
  semCategoria: [],
  totalSemCategoria: 0,
  total: 0,
};
